import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Chargement, EnTetePage, Message, TitreSection } from '../components/charte/Elements';
import SignatureDevis, { ResultatSignature } from '../components/SignatureDevis';
import ListeDocuments from '../components/ListeDocuments';
import { AIDE } from '../aide/textesAide';
import {
  chargerDocuments, DocumentClient, ETATS_A_REGLER, formatHorodatage, formatTotal, messageErreurDocuments,
  messageErreurPdf, telechargerPdf,
} from '../donnees/documents';
import { chargerMissions, devisASigner, idMission, Mission, SignatureDevisServeur } from '../donnees/missions';

/* Documents, d'après la maquette Documents.dc.html : compteurs en
   capitales (devis à signer, factures à régler), filtre par type en
   segments, un bloc indigo par devis à signer (détail et signature), liste
   de tous les documents, deux encadrés (règlement, certification). */

const FILTRES: { cle: string; libelle: string; types: string[] }[] = [
  { cle: 'tous', libelle: 'Tous', types: [] },
  { cle: 'devis', libelle: 'Devis', types: ['DEVIS'] },
  { cle: 'contrats', libelle: 'Contrats', types: ['CONTRAT'] },
  { cle: 'factures', libelle: 'Factures', types: ['FACTURE', 'AVOIR'] },
];

/** Trace de signature, telle que le serveur l'a enregistrée. */
const TraceSignature: React.FC<{ trace: SignatureDevisServeur }> = ({ trace }) => (
  <dl className="petit" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px', marginTop: '8px' }}>
    <dt className="texte-secondaire">Devis</dt><dd className="mono">{trace.devisNumero}</dd>
    <dt className="texte-secondaire">Montant TTC</dt><dd>{formatTotal(trace.montantTtc)}</dd>
    <dt className="texte-secondaire">Signé le</dt><dd>{formatHorodatage(trace.signeLe)} (heure de Dakar, enregistrée par Tribe)</dd>
    {trace.signataire && <><dt className="texte-secondaire">Par</dt><dd>{trace.signataire}</dd></>}
    {trace.adresseIp && <><dt className="texte-secondaire">Adresse IP</dt><dd>{trace.adresseIp}</dd></>}
    <dt className="texte-secondaire">Conditions</dt><dd>{trace.conditionsAcceptees ? 'acceptées' : '—'}</dd>
  </dl>
);

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`;

const Documents: React.FC = () => {
  const { user } = useAuth();
  const { hash } = useLocation();
  const [documents, setDocuments] = useState<DocumentClient[]>([]);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreurDocuments, setErreurDocuments] = useState('');
  const [erreurMissions, setErreurMissions] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState<{ numero: string; trace: SignatureDevisServeur | null } | null>(null);
  const [filtre, setFiltre] = useState('tous');
  const [pdfEnCours, setPdfEnCours] = useState<string | null>(null);
  const [enVue, setEnVue] = useState(hash.startsWith('#devis-') ? hash.slice(1) : '');

  useEffect(() => { document.title = 'Documents — Tribe'; }, []);

  useEffect(() => {
    let actif = true;
    Promise.allSettled([chargerDocuments(), user?.email ? chargerMissions(user.email) : Promise.resolve([])])
      .then(([d, m]) => {
        if (!actif) return;
        if (d.status === 'fulfilled') { setDocuments(d.value); setErreurDocuments(''); }
        else setErreurDocuments(messageErreurDocuments(d.reason));
        if (m.status === 'fulfilled') setMissions(m.value); else setErreurMissions(true);
        setChargement(false);
      });
    return () => { actif = false; };
  }, [user?.email]);

  // Arrivée par un lien « Signer ce devis » (#devis-21) ou bouton « Signer » de la liste : le devis est mis en vue.
  useEffect(() => {
    if (chargement || !enVue) return;
    document.getElementById(enVue)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [chargement, enVue]);
  useEffect(() => { if (hash.startsWith('#devis-')) setEnVue(hash.slice(1)); }, [hash]);
  useEffect(() => {
    if (chargement || hash !== '#comment-regler') return;
    document.getElementById('comment-regler')?.scrollIntoView?.({ block: 'center' });
  }, [chargement, hash]);

  const parId = new Map(missions.map(m => [idMission(m), m]));
  const titreDe = (d: DocumentClient) => parId.get(d.questId ?? '')?.titre ?? d.titreMission ?? '—';
  const enAttente = devisASigner(documents, missions);
  const aRegler = documents.filter(d => d.type === 'FACTURE' && ETATS_A_REGLER.includes(d.etatPaiement || ''));

  const types = FILTRES.find(f => f.cle === filtre)?.types ?? [];
  const visibles = documents
    .filter(d => types.length === 0 || types.includes(d.type))
    .sort((a, b) => (b.emisLe || '').localeCompare(a.emisLe || ''));

  const pdf = async (doc: DocumentClient) => {
    setPdfEnCours(String(doc.id));
    setErreur('');
    try { await telechargerPdf(doc); } catch (err) { setErreur(messageErreurPdf(doc, err)); } finally { setPdfEnCours(null); }
  };

  const signe = ({ devis, mission, trace }: ResultatSignature) => {
    setMissions(ms => ms.map(m => idMission(m) === devis.questId
      ? { ...m, statutPaiement: mission?.statutPaiement ?? 'devis_signe', signatureDevis: trace ?? m.signatureDevis }
      : m));
    setSucces({ numero: trace?.devisNumero ?? devis.numero, trace });
    setEnVue('');
  };

  const surtitre = chargement ? ' ' : [
    enAttente.length > 0 ? `${enAttente.length} devis à signer` : null,
    aRegler.length > 0 ? `${pluriel(aRegler.length, 'facture')} à régler` : null,
  ].filter(Boolean).join(' · ') || 'Rien ne vous attend';

  return (
    <div className="page">
      <EnTetePage
        surtitre={surtitre}
        titre="Documents"
        sousTitre="Devis, contrats, factures et avoirs de toutes vos missions. Montants en F CFA (XOF)."
        aide={AIDE.documents}
        actions={(
          <div className="segments" role="group" aria-label="Filtrer par type">
            {FILTRES.map(f => (
              <button key={f.cle} type="button" aria-pressed={filtre === f.cle} onClick={() => setFiltre(f.cle)}>{f.libelle}</button>
            ))}
          </div>
        )}
      />

      {succes && (
        <Message genre="succes" onFermer={() => setSucces(null)}>
          <strong>Devis {succes.numero} signé.</strong> L'équipe Tribe prépare le lancement de la collecte ;
          une confirmation vous est envoyée par courriel.
          {succes.trace && <TraceSignature trace={succes.trace} />}
        </Message>
      )}
      {erreur && <Message genre="erreur" onFermer={() => setErreur('')}>{erreur}</Message>}
      {erreurMissions && !chargement && (
        <Message genre="attention">
          Vos missions n'ont pas pu être chargées : les devis à signer peuvent ne pas apparaître. Rechargez la page dans un instant.
        </Message>
      )}

      {chargement ? <Chargement /> : (
        <>
          {enAttente.map(d => (
            <SignatureDevis key={d.id} devis={d} mission={parId.get(d.questId ?? '')} titreMission={titreDe(d)}
              enVue={enVue === `devis-${d.id}`} onSigne={signe} />
          ))}

          <section aria-labelledby="titre-tous" className="pile" style={{ gap: '14px' }}>
            <TitreSection titre={filtre === 'tous' ? 'Tous les documents' : FILTRES.find(f => f.cle === filtre)?.libelle ?? ''}
              id="titre-tous" aide={AIDE.factures} />
            {erreurDocuments ? <Message genre="erreur">{erreurDocuments}</Message> : (
              <ListeDocuments documents={visibles} missions={missions} pdfEnCours={pdfEnCours} onPdf={pdf}
                onSigner={d => setEnVue(`devis-${d.id}`)}
                vide={documents.length === 0 ? 'Aucun document émis pour le moment.' : 'Aucun document de ce type.'} />
            )}
          </section>
        </>
      )}

      <section className="grille-2" aria-label="À savoir">
        <div id="comment-regler" className="carte" style={{ padding: '20px 22px', gap: '8px' }}>
          <span className="etiquette-mono" style={{ color: 'var(--ocre-texte)' }}>Comment régler une facture</span>
          <p style={{ fontSize: '14px', lineHeight: 1.55 }}>
            Virement bancaire sur le RIB indiqué sur la facture, ou mobile money selon les indications portées sur la facture.
            Mentionnez le numéro de facture en référence : le règlement est rapproché sous 2 jours ouvrés et votre portail
            passe la facture en « réglée ».
          </p>
        </div>
        <div className="carte" style={{ padding: '20px 22px', gap: '8px' }}>
          <span className="etiquette-mono" style={{ color: 'var(--bleu)' }}>Vos factures sont certifiées</span>
          <p style={{ fontSize: '14px', lineHeight: 1.55 }}>
            Les factures émises par Tribe Sénégal SAS portent NINEA et RCCM. Les factures de Tribe Côte d'Ivoire seront
            certifiées par la DGI (facture normalisée électronique) et porteront un QR code de vérification.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Documents;
